import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Button, Badge } from '@/components/atoms';
import { useWarehouseDetail, useProductAvailability } from '@/hooks/useErpApi';

interface WarehouseDetailProps {
  warehouseId: string;
  onEditClick?: () => void;
}

export const WarehouseDetail: React.FC<WarehouseDetailProps> = ({ warehouseId, onEditClick }) => {
  const navigate = useNavigate();
  const { data: warehouse, isLoading, error, refetch } = useWarehouseDetail(warehouseId);
  const { data: allAvailability } = useProductAvailability();

  // Extract warehouse details from grouped availability structure
  const batches = useMemo(() => {
    if (!allAvailability) return [];
    const warehouseBatches: Array<{
      id: string;
      variant_id: string;
      product_name: string;
      total_quantity: number;
      expired_quantity: number;
      expiry_date: string;
      expiry_status: 'fresh' | 'expiring_soon' | 'expired';
      is_expired: boolean;
    }> = [];

    allAvailability.forEach((product: any) => {
      if (product.warehouse_details) {
        product.warehouse_details.forEach((warehouse: any) => {
          if (warehouse.warehouse_id === warehouseId) {
            warehouseBatches.push({
              id: `${product.variant_id}-${warehouse.warehouse_id}`, // Generate ID
              variant_id: product.variant_id,
              product_name: product.product_name,
              total_quantity: warehouse.quantity,
              expired_quantity: warehouse.expired_quantity,
              expiry_date: warehouse.earliest_expiry,
              expiry_status: warehouse.expiry_status,
              is_expired: warehouse.expiry_status === 'expired',
            });
          }
        });
      }
    });

    return warehouseBatches;
  }, [allAvailability, warehouseId]);

  const [activeTab, setActiveTab] = useState<'overview' | 'batches'>('overview');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <Icon name="Warehouse" size={48} className="text-neutral-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-neutral-600">Loading warehouse details...</p>
        </div>
      </div>
    );
  }

  if (error || !warehouse) {
    return (
      <div className="p-8">
        <div className="bg-error-50 border border-error-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="bg-error-100 p-2 rounded-lg">
              <Icon name="Info" size={24} className="text-error-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-error-900 mb-1">
                Failed to Load Warehouse
              </h3>
              <p className="text-error-700 mb-3">
                Unable to fetch warehouse details. Please try again.
              </p>
              <div className="flex gap-2">
                <Button variant="primary" size="sm" onClick={() => refetch()}>
                  <Icon name="RefreshCw" size={16} className="mr-2" />
                  Retry
                </Button>
                <Button variant="secondary" size="sm" onClick={() => navigate('/warehouses')}>
                  <Icon name="ArrowLeft" size={16} className="mr-2" />
                  Back to List
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totalBatches = batches.length;
  const lowStockBatches = batches.filter((b) => b.total_quantity < 10).length;
  // Use expiry_status from new structure instead of manual calculation
  const expiringBatches = batches.filter((b) => 
    b.expiry_status === 'expiring_soon' || b.expiry_status === 'expired'
  ).length;

  const fullAddress = warehouse.address
    ? [
        warehouse.address.house,
        warehouse.address.street,
        warehouse.address.landmark,
        warehouse.address.vtc,
        warehouse.address.district,
        warehouse.address.state,
        warehouse.address.pincode,
      ]
        .filter(Boolean)
        .join(', ')
    : 'No address available';

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="secondary" size="sm" onClick={() => navigate('/warehouses')}>
          <Icon name="ArrowLeft" size={16} className="mr-2" />
          Back
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-neutral-900">{warehouse.name}</h1>
          <p className="text-neutral-600 mt-1">{fullAddress}</p>
        </div>
        {onEditClick && (
          <Button variant="primary" onClick={onEditClick}>
            <Icon name="Pencil" size={20} className="mr-2" />
            Edit Warehouse
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-neutral-600">Total Batches</p>
            <div className="bg-primary-50 p-2 rounded-lg">
              <Icon name="Package" size={20} className="text-primary-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-neutral-900">{totalBatches}</p>
          <p className="text-sm text-neutral-500 mt-1">Active inventory batches</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-neutral-600">Low Stock Items</p>
            <div className="bg-warning-50 p-2 rounded-lg">
              <Icon name="Info" size={20} className="text-warning-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-warning-700">{lowStockBatches}</p>
          <p className="text-sm text-neutral-500 mt-1">Below threshold</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-neutral-600">Expiring Soon</p>
            <div className="bg-error-50 p-2 rounded-lg">
              <Icon name="Clock" size={20} className="text-error-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-error-700">{expiringBatches}</p>
          <p className="text-sm text-neutral-500 mt-1">Within 30 days</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
        <div className="border-b border-neutral-200">
          <div className="flex gap-4 px-6">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'overview'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-neutral-600 hover:text-neutral-900'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('batches')}
              className={`py-4 px-2 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'batches'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-neutral-600 hover:text-neutral-900'
              }`}
            >
              Batches ({totalBatches})
            </button>
          </div>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-neutral-900 mb-4">
                  Warehouse Information
                </h3>
                <dl className="grid grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-neutral-500 mb-1">Warehouse ID</dt>
                    <dd className="text-sm text-neutral-900">{warehouse.id}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-neutral-500 mb-1">Status</dt>
                    <dd>
                      <Badge variant="success">Active</Badge>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-neutral-500 mb-1">Created On</dt>
                    <dd className="text-sm text-neutral-900">
                      {new Date(warehouse.created_at).toLocaleDateString('en-IN', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-neutral-500 mb-1">Last Updated</dt>
                    <dd className="text-sm text-neutral-900">
                      {new Date(warehouse.updated_at).toLocaleDateString('en-IN', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </dd>
                  </div>
                </dl>
              </div>

              {warehouse.address && (
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900 mb-4">Address Details</h3>
                  <dl className="grid grid-cols-2 gap-4">
                    {warehouse.address.house && (
                      <div>
                        <dt className="text-sm font-medium text-neutral-500 mb-1">
                          Building/House
                        </dt>
                        <dd className="text-sm text-neutral-900">{warehouse.address.house}</dd>
                      </div>
                    )}
                    {warehouse.address.street && (
                      <div>
                        <dt className="text-sm font-medium text-neutral-500 mb-1">Street</dt>
                        <dd className="text-sm text-neutral-900">{warehouse.address.street}</dd>
                      </div>
                    )}
                    {warehouse.address.landmark && (
                      <div>
                        <dt className="text-sm font-medium text-neutral-500 mb-1">Landmark</dt>
                        <dd className="text-sm text-neutral-900">{warehouse.address.landmark}</dd>
                      </div>
                    )}
                    {warehouse.address.vtc && (
                      <div>
                        <dt className="text-sm font-medium text-neutral-500 mb-1">City/Town</dt>
                        <dd className="text-sm text-neutral-900">{warehouse.address.vtc}</dd>
                      </div>
                    )}
                    {warehouse.address.district && (
                      <div>
                        <dt className="text-sm font-medium text-neutral-500 mb-1">District</dt>
                        <dd className="text-sm text-neutral-900">{warehouse.address.district}</dd>
                      </div>
                    )}
                    {warehouse.address.state && (
                      <div>
                        <dt className="text-sm font-medium text-neutral-500 mb-1">State</dt>
                        <dd className="text-sm text-neutral-900">{warehouse.address.state}</dd>
                      </div>
                    )}
                    {warehouse.address.pincode && (
                      <div>
                        <dt className="text-sm font-medium text-neutral-500 mb-1">Pincode</dt>
                        <dd className="text-sm text-neutral-900">{warehouse.address.pincode}</dd>
                      </div>
                    )}
                    {warehouse.address.country && (
                      <div>
                        <dt className="text-sm font-medium text-neutral-500 mb-1">Country</dt>
                        <dd className="text-sm text-neutral-900">{warehouse.address.country}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              )}
            </div>
          )}

          {activeTab === 'batches' && (
            <div>
              {batches.length === 0 ? (
                <div className="text-center py-12">
                  <Icon name="Package" size={64} className="text-neutral-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-neutral-900 mb-2">No Batches Found</h3>
                  <p className="text-neutral-600">
                    This warehouse does not have any inventory batches yet.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-neutral-50 border-b border-neutral-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Batch Number
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Product
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Available Qty
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Expiry Date
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-neutral-200">
                      {batches.map((batch) => {
                        const expiryDate = new Date(batch.expiry_date);
                        const isLowStock = batch.total_quantity < 10;
                        const isExpired = batch.is_expired || batch.expiry_status === 'expired';
                        const isExpiringSoon = batch.expiry_status === 'expiring_soon';

                        return (
                          <tr
                            key={batch.id}
                            onClick={() => navigate(`/batches/${batch.variant_id}`)}
                            className="hover:bg-neutral-50 cursor-pointer transition-colors"
                          >
                            <td className="px-4 py-4 whitespace-nowrap">
                              <span className="text-sm font-medium text-neutral-900">
                                {batch.variant_id.substring(0, 12)}
                              </span>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div>
                                <span className="text-sm text-neutral-900">{batch.product_name}</span>
                                {batch.expired_quantity > 0 && (
                                  <span className="text-xs text-error-600 block">
                                    {batch.expired_quantity} expired
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <span
                                className={`text-sm font-medium ${
                                  isLowStock ? 'text-warning-700' : 'text-neutral-900'
                                }`}
                              >
                                {batch.total_quantity}
                              </span>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <span
                                className={`text-sm ${
                                  isExpired
                                    ? 'text-error-700 font-medium'
                                    : isExpiringSoon
                                      ? 'text-warning-700'
                                      : 'text-neutral-600'
                                }`}
                              >
                                {expiryDate.toLocaleDateString()}
                              </span>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              {batch.expiry_status === 'expired' ? (
                                <Badge variant="error">Expired</Badge>
                              ) : batch.expiry_status === 'expiring_soon' ? (
                                <Badge variant="warning">Expiring Soon</Badge>
                              ) : isLowStock ? (
                                <Badge variant="warning">Low Stock</Badge>
                              ) : (
                                <Badge variant="success">Active</Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
